<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2021-2023 (original work) Open Assessment Technologies SA ;
    import { Icon, IconBarButton } from '@oat-sa-private/ui-elements';
    import Button from '@oat-sa-private/ui-elements/button/Button.svelte';
    import ButtonLink from '@oat-sa-private/ui-elements/buttonLink/ButtonLink.svelte';
    import { createEventDispatcher } from 'svelte';
    import hierarchyOptions from './hierarchyOptions.js';
    import { __ } from '@oat-sa-private/ui-core';

    /**
     * @typedef {Object} NotificationAction
     * @property {string} action - A string identifying a user action to be displayed on the notification
     * @property {string} title - A string containing action text to be shown to the user
     * @property {'link'|'button'} type - A string indicating the type of action to be displayed
     * @property {string} [href] - A string containing the URL to be opened when the action is clicked (only for type 'link')
     * @property {object} componentProperties - An object containing properties and attributes to be passed to the action component (href, target, etc)
     */

    const dispatch = createEventDispatcher();

    export let key;
    export let title = null;
    export let message = null;
    export let data;
    export let hierarchy;
    export let ariaLive = 'assertive';
    export let closeable = false;
    export let closeButtonProps = {
        icon: 'remove-12',
        size: 'base-12',
        label: __('close'),
        ariaLabel: __('close')
    };

    /**
     * @type {NotificationAction[]}
     */
    export let actions = [];

    const hierarchyIcons = {
        [hierarchyOptions.neutral]: 'info-bare-16',
        [hierarchyOptions.success]: 'check-16',
        [hierarchyOptions.warning]: 'warning-unframed-16',
        [hierarchyOptions.alert]: 'lightning-16'
    };

    $: hierarchy = typeof hierarchy === 'undefined' ? hierarchyOptions.neutral : hierarchy;
    $: badge = hierarchyIcons[hierarchy];

    function notificationCloseHandler() {
        dispatch('notificationclose', { key, data });
    }

    function notificationActionHandler({ event, type, action }) {
        if (typeof action === 'function') {
            dispatch('notificationclick', { key, data });
            action(event, { key, data });
        } else {
            if (type === 'link' || type === 'button-link') {
                event.preventDefault();
            }

            dispatch('notificationclick', { key, action, data });
        }
    }
</script>

<style>
    .notification-wrapper {
        color: var(--color-text-info);
        background-color: var(--bg-color);
        border-color: var(--accent-color);
        display: flex;
        align-items: stretch;
        border-style: solid;
        padding: var(--space-1x5);
        position: relative;
        border-width: 1px;
    }

    .notification-wrapper > :global(svg) {
        color: var(--accent-color);
        margin-top: calc(var(--space-1x) + 3px);
    }

    .notification-close > :global(.icon-bar-btn) {
        color: currentColor;
    }

    .notification-content {
        display: flex;
        padding: var(--space-1x);
        flex-grow: 1;
        flex-wrap: wrap;
    }

    .notification-text {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        flex-shrink: 0;
        max-width: 100%;
    }

    .notification-heading + .notification-body {
        margin-top: var(--space-1x);
    }

    .notification-actions {
        display: flex;
        flex-direction: row;
        flex-grow: 0;
        flex-shrink: 0;
        align-items: center;
        list-style: none;
        padding: 0;
        margin: 0;
        --spacing: var(--space-1x);
    }

    .notification-actions li {
        display: flex;
        align-items: center;
    }

    .notification-actions > :global(* + *) {
        margin-left: var(--spacing);
    }

    .notification-actions > :global(* + .type_link::before),
    .notification-actions > :global(* + .type_button-link::before) {
        content: '-';
        margin-right: var(--spacing);
        text-decoration: none;
    }

    .notification-wrapper.neutral {
        --bg-color: var(--color-brand-light);
        --accent-color: var(--color-brand);
    }
    .notification-wrapper.success {
        --bg-color: var(--color-success-alternative-bg);
        --accent-color: var(--color-success);
    }
    .notification-wrapper.warning {
        --bg-color: var(--color-partial-alternative-bg);
        --accent-color: var(--color-partial);
    }
    .notification-wrapper.alert {
        --bg-color: var(--color-alert-alternative-bg);
        --accent-color: var(--color-alert);
    }
</style>

<div class={`notification-wrapper button-fill ${hierarchy}`}>
    <Icon name={badge} ariaHidden={true} />
    <div class="notification-content" aria-live={ariaLive}>
        <div class="notification-text">
            {#if title}
                <span class="notification-heading ui-heading">{title}</span>
            {/if}
            <slot name="notification-content">
                {#if message}
                    <span class="notification-body">{message}</span>
                {/if}
            </slot>
        </div>
        {#if actions.length}
            <ul class="notification-actions">
                {#each actions as { type, action, title, href, skin, componentProperties, iconBefore, iconAfter }}
                    <li class={`type_${type}`}>
                        {#if type === 'link' || type === 'button-link'}
                            <ButtonLink
                                on:click={event => notificationActionHandler({ event, type, action })}
                                target={(componentProperties && componentProperties.target) || '_blank'}
                                {...{
                                    title,
                                    href,
                                    iconBefore,
                                    iconAfter
                                }} />
                        {:else}
                            <Button
                                {...{
                                    ...componentProperties,
                                    label: title,
                                    skin: skin ? skin : 'primary',
                                    size: 'small',
                                    shape: 'pill'
                                }}
                                on:click={event => notificationActionHandler({ event, type, action })} />
                        {/if}
                    </li>
                {/each}
            </ul>
        {/if}
    </div>
    {#if closeable}
        <div class="notification-close">
            <IconBarButton
                icon={closeButtonProps.icon}
                size={closeButtonProps.size}
                label={closeButtonProps.label}
                ariaLabel={closeButtonProps.ariaLabel}
                on:click={notificationCloseHandler} />
        </div>
    {/if}
</div>
